import "./App.css";
import { App, ConfigProvider } from "antd";
import theme from "./theme";
import NarinRouter from "./Router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { VITE_PROD_MODE, VITE_SENTRY_DSN } from "./environments";
import * as Sentry from "@sentry/react";
import { useMediaQuery } from "react-responsive";
import { IS_BREAKPOINT_DESKTOP } from "constants/common";
import useIsDesktopStore from "stores/isDesktop";
import { useEffect, useLayoutEffect } from "react";
import { identifyUser, initAnalytics } from "./utils/analytics";
import useUserStore from "stores/user";

const queryClient = new QueryClient();
if (VITE_PROD_MODE) {
  // Initialize analytics (Mixpanel)
  initAnalytics();

  // Initialize Sentry
  Sentry.init({
    dsn: VITE_SENTRY_DSN,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    // Tracing
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ["localhost"],
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  });
}

function NarinApp() {
  const { setIsDesktop } = useIsDesktopStore();
  const { user } = useUserStore();

  const isDesktop = useMediaQuery({
    query: IS_BREAKPOINT_DESKTOP,
  });

  useLayoutEffect(() => {
    setIsDesktop(isDesktop);
  }, [isDesktop]);

  // Check for returning users and identify them
  useEffect(() => {
    // If user is authenticated and has userData, identify them as returning users
    if (user) {
      identifyUser(user.email, {
        userId: user.id,
        name: user.fullName,
        lastLogin: new Date().toISOString(),
        isReturningUser: true,
      });
    }
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ConfigProvider theme={theme}>
        <App>
          <NarinRouter />
        </App>
      </ConfigProvider>
    </QueryClientProvider>
  );
}

export default NarinApp;
